import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { auditLogService } from '../services/api';

const EVENT_LABELS = {
  'login_success': { label: 'Login Success', color: '#228b22' },
  'signup': { label: 'New Signup', color: '#1565c0' },
  'otp_requested': { label: 'OTP Requested', color: '#666' },
  'otp_failed': { label: 'OTP Failed', color: '#dc3545' },
  'otp_verified': { label: 'OTP Verified', color: '#228b22' },
  'login_failed': { label: 'Login Failed', color: '#dc3545' },
  'token_refresh': { label: 'Token Refresh', color: '#666' },
  'token_revoked': { label: 'Token Revoked', color: '#e65100' },
  'logout': { label: 'Logout', color: '#666' },
  'session_resume': { label: 'Session Resume', color: '#1565c0' },
  'admin_create': { label: 'Admin Created User', color: '#7b1fa2' },
  'admin_migrate': { label: 'Admin Migration', color: '#7b1fa2' },
  'admin_bulk_migrate': { label: 'Bulk Migration', color: '#7b1fa2' },
  // V208: portfolio + invitation lifecycle (server accepts these as filters;
  // omitting them here would make them render but be unfilterable).
  'tenant_created': { label: 'Tenant created', color: '#1565c0' },
  'invite_sent': { label: 'Invite sent', color: '#666' },
  'invite_accepted': { label: 'Invite accepted', color: '#228b22' },
  'invite_revoked': { label: 'Invite revoked', color: '#e65100' },
  // V276: approval-gate + contact-proof + product lifecycle events. Colors keep
  // the page's semantics: green = success, red = refusal/removal, orange =
  // policy gate/revocation, blue = informational, grey = request in flight.
  'login_blocked': { label: 'Login blocked (approval gate)', color: '#e65100' },
  'access_approved': { label: 'Access approved', color: '#228b22' },
  'access_rejected': { label: 'Access rejected', color: '#dc3545' },
  'contact_verify_requested': { label: 'Contact verification requested', color: '#666' },
  'contact_verified': { label: 'Contact verified', color: '#228b22' },
  'auto_approved_via_invite': { label: 'Auto-approved via invite', color: '#1565c0' },
  'product_enrolled': { label: 'Product enrolled', color: '#228b22' },
  'product_unenrolled': { label: 'Product unenrolled', color: '#e65100' },
  // V277: door policy lifecycle. Purple = operator/admin act; blue =
  // informational auto-approval (mechanism-named, like auto_approved_via_invite).
  'door_policy_changed': { label: 'Door policy changed', color: '#7b1fa2' },
  'auto_approved_open_door': { label: 'Auto-approved (open door)', color: '#1565c0' },
};

const AuditLog = () => {
  const [searchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    search: '',
    eventType: searchParams.get('eventType') || '',
    userId: searchParams.get('userId') || '',
  });
  const [summary, setSummary] = useState({});
  const [error, setError] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
      };
      if (filters.search) params.search = filters.search;
      if (filters.eventType) params.eventType = filters.eventType;
      if (filters.userId) params.userId = filters.userId;

      const response = await auditLogService.getAll(params);
      setEntries(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({ ...prev, ...response.pagination }));
      }
      if (response.summary) {
        setSummary(response.summary);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
      setError('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters.search, filters.eventType, filters.userId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'medium',
    });
  };

  const formatRelative = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Top summary cards
  const totalEvents = Object.values(summary).reduce((a, b) => a + b, 0);
  const loginCount = (summary.login_success || 0) + (summary.session_resume || 0);
  const failedCount = (summary.otp_failed || 0) + (summary.login_failed || 0);
  const signupCount = summary.signup || 0;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Login Audit Log</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ padding: '0.75rem 1rem', background: '#f5f5f5', borderRadius: '8px', minWidth: '120px' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{totalEvents}</div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Events</div>
        </div>
        <div style={{ padding: '0.75rem 1rem', background: '#e6f7e6', borderRadius: '8px', minWidth: '120px' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#228b22' }}>{loginCount}</div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Logins</div>
        </div>
        <div style={{ padding: '0.75rem 1rem', background: '#ffe6e6', borderRadius: '8px', minWidth: '120px' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#dc3545' }}>{failedCount}</div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Failed</div>
        </div>
        <div style={{ padding: '0.75rem 1rem', background: '#e3f2fd', borderRadius: '8px', minWidth: '120px' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1565c0' }}>{signupCount}</div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Signups</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by email or identifier..."
            value={filters.search}
            onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPagination({ ...pagination, page: 1 }); }}
          />
        </div>
        <select
          value={filters.eventType}
          onChange={(e) => { setFilters({ ...filters, eventType: e.target.value }); setPagination({ ...pagination, page: 1 }); }}
        >
          <option value="">All Events</option>
          {Object.entries(EVENT_LABELS).map(([key, val]) => (
            <option key={key} value={key}>{val.label} {summary[key] ? `(${summary[key]})` : ''}</option>
          ))}
        </select>
        {filters.userId && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => { setFilters({ ...filters, userId: '' }); setPagination({ ...pagination, page: 1 }); }}
          >
            ✕ Clear user filter
          </button>
        )}
      </div>

      {/* Audit Log Table */}
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>User</th>
                  <th>Identifier</th>
                  <th>IP Address</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-state">No audit log entries found</td>
                  </tr>
                ) : (
                  entries.map((entry) => {
                    const evt = EVENT_LABELS[entry.eventType] || { label: entry.eventType, color: '#666' };
                    return (
                      <tr key={entry.id}>
                        <td>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            background: `${evt.color}15`,
                            color: evt.color,
                            border: `1px solid ${evt.color}30`,
                            whiteSpace: 'nowrap',
                          }}>
                            {evt.label}
                          </span>
                        </td>
                        <td>
                          {entry.userEmail ? (
                            <Link to={`/users/${encodeURIComponent(entry.userEmail)}`}>
                              {entry.userName || entry.userEmail}
                            </Link>
                          ) : (
                            <span style={{ color: '#999' }}>—</span>
                          )}
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>{entry.identifier || '—'}</td>
                        <td className="mono" style={{ fontSize: '0.8rem' }}>{entry.ipAddress || '—'}</td>
                        <td title={formatDate(entry.createdAt)} style={{ whiteSpace: 'nowrap' }}>
                          {formatRelative(entry.createdAt)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalCount > 0 && pagination.totalPages > 1 && (
            <div className="pagination">
              <span>Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} events</span>
              <div className="pagination-buttons">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                >
                  ← Previous
                </button>
                <button
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLog;
